import React, { FC, useCallback, useRef, useState } from 'react';
import { ListActivePaymentDto, PaymentMethodDto, PaymentState } from '../../../../../../generated-api';
import classNames from 'classnames';
import CashButton from './CashButton';
import IntegratedTerminalPaymentButton from './IntegratedTerminalPaymentButton';
import UnintegratedPaymentButton from './UnintegratedPaymentButton';
import { useTerminalState } from '../../../../../context/PaymentMethodStore';
import { useF1NetworkState } from '../../../../../context/F1NetworkStateStore';

// Keeps the button in loading state until the terminal polling catches up (reports BUSY).
// Acts as a safety net — clears automatically if the terminal never goes BUSY.
const EXTENDED_LOADING_MS = 15000;

interface Props {
  paymentMethod: PaymentMethodDto;
  activePayment?: ListActivePaymentDto;
  initiatePayment: (
    paymentMethod: PaymentMethodDto,
    dynamicFields?: { [key: string]: string },
    promptTips?: boolean
  ) => void;
  setFocusedPayment: (activePayment: ListActivePaymentDto) => void;
  shouldGrow: boolean;
  canTryPaymentAnyway: boolean;
}

const PaymentButton: FC<Props> = ({
  paymentMethod,
  activePayment,
  initiatePayment,
  setFocusedPayment,
  shouldGrow,
  canTryPaymentAnyway,
}) => {
  const isCash = paymentMethod.paymentMethod == 'CASH';
  const terminalState = useTerminalState(paymentMethod.id);
  const { isOffline } = useF1NetworkState();

  const [extendedLoading, setExtendedLoading] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Keep showing loading until the terminal polling catches up (reports BUSY),
  // so the button doesn't briefly re-enable between the POST returning and
  // the terminal state updating.
  const isTerminalLoading = extendedLoading && terminalState !== 'BUSY';

  const className = classNames({
    'flex-col ml-2 first-of-type:ml-4 last-of-type:mr-4': true,
    'opacity-75': terminalState === 'BUSY',
    'flex-1': shouldGrow && !isCash,
    'flex-none': !shouldGrow,
    'bg-grey-medium text-grey-dark border-grey-medium cursor-not-allowed': terminalState === 'OFFLINE' || isOffline,
  });
  const handleButtonClick = useCallback(
    (ignoreTerminalState = false) => {
      if (isOffline) return;

      if (activePayment?.paymentState === PaymentState.AWAITING_PAYMENT) {
        setFocusedPayment(activePayment);
        return;
      }

      // POST in-flight for this button — block re-initiation but allow active-payment routing above.
      if (extendedLoading) return;

      if (!ignoreTerminalState && terminalState !== 'AVAILABLE') return;

      clearTimeout(timeoutRef.current);
      setExtendedLoading(true);
      timeoutRef.current = setTimeout(() => setExtendedLoading(false), EXTENDED_LOADING_MS);

      initiatePayment(paymentMethod, undefined, true);
    },
    [activePayment, extendedLoading, initiatePayment, isOffline, paymentMethod, setFocusedPayment, terminalState]
  );

  if (isCash) {
    return (
      <CashButton
        className={className}
        paymentMethod={paymentMethod}
        handleButtonClick={handleButtonClick}
        activePayment={activePayment}
        busy={terminalState === 'BUSY'}
      />
    );
  }
  if (paymentMethod.provider == 'UNINTEGRATED') {
    return (
      <UnintegratedPaymentButton
        paymentMethod={paymentMethod}
        onClick={initiatePayment}
        className={className}
        busy={terminalState === 'BUSY'}
      />
    );
  }

  return (
    <IntegratedTerminalPaymentButton
      paymentMethod={paymentMethod}
      terminalState={terminalState}
      className={className}
      paymentLoading={isTerminalLoading}
      onClick={() => handleButtonClick()}
      onTryAnyway={() => handleButtonClick(true)}
      canTryPaymentAnyway={canTryPaymentAnyway}
    />
  );
};

export default React.memo(PaymentButton);
